import { KeyRound, Pencil, Power, PowerOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import type { DirectoryUserRow } from '../types'
import { StatusBadge } from './AccessBits'

type Props = {
  rows: DirectoryUserRow[]
  loading: boolean
  canWrite: boolean
  onEdit: (user: DirectoryUserRow) => void
  onToggleActive: (user: DirectoryUserRow) => void
  onResetPassword: (user: DirectoryUserRow) => void
}

export function UsersTable({ rows, loading, canWrite, onEdit, onToggleActive, onResetPassword }: Props) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    )
  }

  return (
    <>
      <ul className="divide-y md:hidden">
        {rows.map((u) => (
          <li key={u.id} className="space-y-3 p-3">
            <div className="flex items-start gap-3">
              <Avatar name={u.name} src={u.avatarUrl} className="size-9" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{u.name}</div>
                <div className="truncate text-xs text-muted-foreground">{u.email}</div>
              </div>
              <StatusBadge status={u.isActive ? 'active' : 'passive'} />
            </div>
            {u.roleNames.length ? (
              <div className="flex flex-wrap gap-1 pl-12">
                {u.roleNames.slice(0, 3).map((role) => (
                  <Badge key={role} tone="neutral">
                    {role}
                  </Badge>
                ))}
              </div>
            ) : null}
            {canWrite ? (
              <div className="flex justify-end gap-0.5 pl-12">
                <Button size="icon" variant="ghost" className="size-8" aria-label={t('common.edit')} onClick={() => onEdit(u)}>
                  <Pencil />
                </Button>
                <Button size="icon" variant="ghost" className="size-8" aria-label={t('access.directory.resetPassword')} onClick={() => onResetPassword(u)}>
                  <KeyRound />
                </Button>
                <Button size="icon" variant="ghost" className="size-8" aria-label={u.isActive ? t('access.directory.deactivate') : t('access.directory.activate')} onClick={() => onToggleActive(u)}>
                  {u.isActive ? <PowerOff /> : <Power />}
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      <div className="hidden md:block">
        <Table>
          <THead>
            <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
              <TH>{t('access.directory.columns.name')}</TH>
              <TH className="hidden lg:table-cell">{t('access.directory.columns.roles')}</TH>
              <TH className="hidden xl:table-cell">{t('access.directory.columns.department')}</TH>
              <TH>{t('access.directory.columns.status')}</TH>
              {canWrite ? <TH className="w-28 text-right">{t('access.directory.columns.actions')}</TH> : null}
            </TR>
          </THead>
          <TBody>
            {rows.map((u) => (
              <TR key={u.id}>
                <TD>
                  <div className="flex items-center gap-3">
                    <Avatar name={u.name} src={u.avatarUrl} className="size-9" />
                    <div className="min-w-0">
                      <p className="font-medium">{u.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                    </div>
                  </div>
                </TD>
                <TD className="hidden lg:table-cell">
                  {u.roleNames.length ? (
                    <div className="flex flex-wrap gap-1">
                      {u.roleNames.slice(0, 3).map((role) => (
                        <Badge key={role} tone="neutral">
                          {role}
                        </Badge>
                      ))}
                      {u.roleNames.length > 3 ? <span className="text-xs text-muted-foreground">+{u.roleNames.length - 3}</span> : null}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </TD>
                <TD className="hidden text-muted-foreground xl:table-cell">{u.departmentName || u.position || '—'}</TD>
                <TD>
                  <StatusBadge status={u.isActive ? 'active' : 'passive'} />
                </TD>
                {canWrite ? (
                  <TD className="text-right">
                    <div className="flex justify-end gap-0.5">
                      <Tooltip side="top" content={t('common.edit')}>
                        <Button size="icon" variant="ghost" className="size-8" aria-label={t('common.edit')} onClick={() => onEdit(u)}>
                          <Pencil />
                        </Button>
                      </Tooltip>
                      <Tooltip side="top" content={t('access.directory.resetPassword')}>
                        <Button size="icon" variant="ghost" className="size-8" aria-label={t('access.directory.resetPassword')} onClick={() => onResetPassword(u)}>
                          <KeyRound />
                        </Button>
                      </Tooltip>
                      <Tooltip side="top" content={u.isActive ? t('access.directory.deactivate') : t('access.directory.activate')}>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="size-8"
                          aria-label={u.isActive ? t('access.directory.deactivate') : t('access.directory.activate')}
                          onClick={() => onToggleActive(u)}
                        >
                          {u.isActive ? <PowerOff /> : <Power />}
                        </Button>
                      </Tooltip>
                    </div>
                  </TD>
                ) : null}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </>
  )
}
